import {useRef, useState} from 'react';
import {view, Action, Icon, Divider, Tooltip, tokens} from '@sail/ui';
import {clipboard, link} from '@sail/icons/react/Icon';
import {useIntl as useSailIntl} from '@sail/intl';
import {defineMessages, useIntl} from 'react-intl';
import {useHover} from '@sail/react-aria';
import {copyToClipboard} from '@sail/utils';
import {AgentCallResponse} from '@stripe-internal/conversations-client';
import {useWidget} from '../../../Widget/useWidget';

type PaymentLinkCardProps = {
  agentCallResponse: AgentCallResponse;
  onClick?: () => void;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.payment_link_card.title',
    description: 'Payment Link Card title',
    defaultMessage: 'Payment link',
  },
});

export const PaymentLinkCard = ({
  agentCallResponse,
  onClick,
}: PaymentLinkCardProps) => {
  const intl = useIntl();
  const sailIntl = useSailIntl();
  const widget = useWidget();
  const copyLinkRef = useRef<HTMLDivElement>(null);
  const [isCopied, setIsCopied] = useState(false);
  const {hoverProps, isHovered} = useHover({});
  const paymentLink = agentCallResponse.payment_link;
  const lineItems = agentCallResponse.line_items;

  if (!lineItems || lineItems.length === 0 || !paymentLink) {
    return null;
  }

  const {id, url} = paymentLink || {};
  const product = lineItems[0];
  const {
    description: productName,
    amount_total: price,
    currency,
  } = product || {};
  const productId = product?.price?.product || product?.id;

  return (
    <view.div
      data-testid="payment-link-card"
      css={{
        backgroundColor: 'surface',
        borderRadius: 'medium',
        paddingTop: 'small',
        paddingBottom: 'space.150',
        paddingLeft: 'space.150',
        paddingRight: 'space.150',
        borderColor: 'border',
        borderWidth: 'small',
        borderStyle: 'solid',
        stack: 'y',
        gap: 'small',
        maxWidth: '400px',
        position: 'relative',
        transition: 'box-shadow 0.5s',
        ':hover': {
          cursor: 'pointer',
          boxShadow: tokens.shadow.large.toString(),
        },
      }}
      {...hoverProps}
      onClick={() => {
        widget.requestAction({
          name: 'navigate',
          data: {
            href: `/payment-links/${id}`,
          },
        });
        onClick?.();
      }}
    >
      <view.div
        css={{
          stack: 'x',
          gap: tokens.space[75].toString(),
          alignItems: 'center',
        }}
      >
        <Icon
          icon={link}
          size="xsmall"
          css={{color: isHovered ? 'action.primary' : 'subdued'}}
        />
        <view.span
          css={{
            color: 'subdued',
            font: 'label.small.emphasized',
          }}
        >
          {intl.formatMessage(messages.cardTitle)}
        </view.span>
        {productName ? (
          <view.div css={{height: 'space.125', stack: 'x', width: 'space.0'}}>
            <Divider />
          </view.div>
        ) : null}
        {productName ? (
          <view.span
            css={{
              font: 'label.small',
              color: 'action.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              ':hover': {
                color: 'action.primary.hovered',
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              widget.requestAction({
                name: 'navigate',
                data: {
                  href: `/products/${productId}`,
                  rotue: {
                    name: 'PRODUCTS_DETAIL',
                    params: {id: productId},
                  },
                },
              });
            }}
          >
            {productName}
          </view.span>
        ) : null}
      </view.div>
      {price && currency ? (
        <view.span
          css={{
            font: 'heading.large',
          }}
        >
          {sailIntl.formatCurrency(price, {currency})}
        </view.span>
      ) : null}
      <Action
        onPress={() => {
          if (url) {
            copyToClipboard(url);
            setIsCopied(true);
            setTimeout(() => {
              setIsCopied(false);
            }, 2000);
          }
        }}
      >
        <Tooltip
          trigger={copyLinkRef}
          placement="top"
          css={{
            padding: 'small',
            color: 'onFeedback.neutral.emphasized',
          }}
        >
          {isCopied ? 'Copied!' : 'Copy link'}
        </Tooltip>
        <view.div
          css={{
            borderRadius: 'medium',
            padding: 'small',
            borderColor: '#ebeef1',
            borderWidth: 'small',
            borderStyle: 'solid',
            backgroundColor: isCopied ? tokens.color.brand[50] : 'offset',
            display: 'flex',
            overflow: 'hidden',
            justifyContent: 'space-between',
            alignItems: 'center',
            ':hover': {
              cursor: 'pointer',
            },
          }}
        >
          <view.span
            css={{
              font: 'label.small.emphasized',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: 'monospace',
              flexGrow: 1,
              flexShrink: 1,
            }}
          >
            {url}
          </view.span>
          <Icon
            ref={copyLinkRef}
            icon={clipboard}
            size="xsmall"
            css={{
              fill: 'action.primary',
              flexShrink: 0,
              marginLeft: 'space.50',
            }}
          />
        </view.div>
      </Action>
    </view.div>
  );
};
